import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { ExternalLink, Loader2 } from 'lucide-react';
import { PlanType } from '@optidev/shared-types';

import type { FreeDailyCredits } from '../../../lib/api/credits';

interface CurrentPlanCardWithCreditsProps {
  planType: PlanType;
  amount: number;
  currency: string;
  interval: 'month' | 'year' | null;
  currentPeriodEnd: string | null;
  onManageBilling: () => void;
  onTopUp: () => void;
  loading?: boolean;
  cancelAtPeriodEnd?: boolean;
  pendingPlan?: {
    planType: PlanType;
    amount: number;
    interval: 'month' | 'year';
  } | null;
  hasCardOnFile?: boolean;
  creditBalance: number | null;           // Total (subscription + top-up)
  subscriptionBalance: number | null;     // Subscription credits only
  topupBalance: number | null;            // Top-up credits only
  creditAllocated: number | null;
  creditUsed: number | null;
  creditNextReset: string | null;
  creditsLoading: boolean;
  freeDaily?: FreeDailyCredits | null;    // free daily-credit model state (drives Free-plan copy)
}

export const CurrentPlanCardWithCredits: React.FC<CurrentPlanCardWithCreditsProps> = ({
  planType,
  amount,
  currency,
  interval,
  currentPeriodEnd,
  onManageBilling,
  hasCardOnFile = false,
  loading = false,
  cancelAtPeriodEnd = false,
  pendingPlan = null,
  creditBalance,
  subscriptionBalance,
  topupBalance,
  creditAllocated,
  creditUsed,
  creditNextReset,
  creditsLoading,
  freeDaily = null,
}) => {
  const getPlanName = (plan: PlanType): string => {
    const names: Record<string, string> = {
      [PlanType.FREE]: 'Free',
      [PlanType.STANDARD]: 'Standard',
      [PlanType.PRO]: 'Pro',
      [PlanType.ENTERPRISE]: 'Enterprise',
      [PlanType.EMBED]: 'OptiDev',
    };
    return names[plan] || 'Free';
  };

  const formatPrice = (amount: number, currency: string): string => {
    const symbols: Record<string, string> = { usd: '$', eur: '€', gbp: '£' };
    const symbol = symbols[currency.toLowerCase()] || '$';
    return `${symbol}${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Detect if renewal is processing (creditNextReset is before currentPeriodEnd).
  // Only meaningful for monthly subs — annual subs intentionally have
  // nextResetAt < currentPeriodEnd because credits refill monthly while
  // Stripe bills yearly.
  const isRenewalProcessing = (): boolean => {
    if (interval !== 'month') return false;
    if (!creditNextReset || !currentPeriodEnd) return false;
    return new Date(creditNextReset) < new Date(currentPeriodEnd);
  };

  const isFree = planType === PlanType.FREE;
  const renewalProcessing = isRenewalProcessing();

  return (
    <Card className="border-neutral-200 dark:border-neutral-800 bg-muted/30">
      <CardContent className="p-4">
        {/* Top row: plan info + manage button */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{getPlanName(planType)} Plan</span>
              {!isFree && (
                <span className="text-xs text-muted-foreground">
                  {formatPrice(amount, currency)}{interval && `/${interval}`}
                </span>
              )}
            </div>
            {!isFree && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {renewalProcessing && 'Renewal processing...'}
                {!renewalProcessing && currentPeriodEnd && !cancelAtPeriodEnd && `Renews ${formatDate(currentPeriodEnd)}`}
                {currentPeriodEnd && cancelAtPeriodEnd && `Ends ${formatDate(currentPeriodEnd)}`}
                {cancelAtPeriodEnd && !pendingPlan && ' — will be cancelled at end of billing period'}
                {pendingPlan && ` — changing to ${getPlanName(pendingPlan.planType)} (${formatPrice(pendingPlan.amount, currency)}/${pendingPlan.interval}) on ${formatDate(currentPeriodEnd)}`}
              </p>
            )}
            {isFree && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {freeDaily?.dailyGrant ?? 10} free credits/day (up to{' '}
                {freeDaily?.monthlyCap ?? 50}/month), basic AI models, public app only
              </p>
            )}
          </div>
          {(!isFree || hasCardOnFile) && (
            <Button
              variant="ghost"
              onClick={onManageBilling}
              disabled={loading}
              className="gap-1.5 shrink-0 text-xs h-8 px-3 text-foreground/70 hover:text-foreground border border-neutral-200 dark:border-neutral-700"
            >
              {loading ? 'Loading...' : 'Manage Billing'}
              <ExternalLink className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Credits row */}
        {creditsLoading ? (
          <div className="flex items-center justify-center py-3">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4 pt-3 border-t border-neutral-200/60 dark:border-neutral-800/60">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Credits Available</p>
              <p className="text-lg font-semibold tabular-nums">
                {creditBalance !== null ? creditBalance.toFixed(2) : '0.00'}
              </p>
            </div>
            {!isFree && (
              <>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Plan credits {interval === 'year'
                      ? `(refills monthly${creditNextReset ? ` — next ${formatDate(creditNextReset)}` : ''})`
                      : '(resets each billing cycle)'}
                  </p>
                  <p className="text-lg font-semibold tabular-nums">
                    {subscriptionBalance?.toFixed(2) ?? '0.00'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Top-up credits (never expires)</p>
                  <p className="text-lg font-semibold tabular-nums">
                    {topupBalance?.toFixed(2) ?? '0.00'}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
